import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { DATE_INTERVAL, DEFAULT_LIMIT, DEFAULT_SKIP, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import {
  createSchoolTeam,
  getAllSchoolTeams,
  getSchoolTeams,
  getSchoolTeamsCount,
  updateSchoolTeam
} from 'Src/helpers/service/admin/schoolTeams';
import { getAgesGroupFormatted, getEventAges, getEventAgesGroup } from 'Src/helpers/accessor/accessor';
import {
  getSelectOptionForAge,
  getSelectOptionForHouses,
  getSelectOptionForSport,
  getSelectOptionForTournamentTeamGender
} from 'Src/helpers/table/select';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { getSchoolSports } from 'Src/helpers/service/admin/user';
import { SchoolForm } from 'Src/models/form';
import { SchoolHouse } from 'Src/models/house';
import { SimpleAlert } from 'Src/components/SimpleAlert/SimpleAlert';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { SchoolTeamForm } from './SchoolTeamForm/SchoolTeamForm';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { Button } from 'Src/components/Button/Button';
import { TEAM_TABS } from 'Src/consts/team';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING, TABS } from 'Src/consts/common';
import { getAllSchoolTeamPlayers, updateSchoolTeamPlayersBatch } from 'Src/helpers/service/admin/schoolTeamPlayers';
import { SchoolTeamPlayersSummary } from 'Src/components/SchoolTeamPLayersSummary/SchoolTeamPlayersSummary';
import { SchoolTeamSummary } from 'Src/components/SchoolTeamSummary/SchoolTeamSummary';
import { SchoolTeamKitSummary } from 'Src/components/SchoolTeamKitSummary/SchoolTeamKitSummary';
import { TYPE_OF_PLAYERS } from 'Src/consts/sport';
import { Team } from '../../../../../models/team';
import { Sport } from '../../../../../models/sport';
import { deleteEvent } from '../../../../../helpers/service/admin/schoolEvents';
import { deleteTeam } from '../../../../../helpers/service/admin/schoolTeams';
import moment from 'moment';
import { KitSelector } from 'Src/components/KitLab/KitSelector';
import { getKitLabKits, KitLabBackendKit } from 'Src/helpers/service/admin/kitLab';
import { KitShirtPreview } from 'Src/components/KitLab/KitShirtPreview';
import { getLayoutForKit, resolveDetailColor } from 'Src/helpers/kitLab/kitLab';
import { uploadReport } from 'Src/helpers/service/admin/report';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/report';
import { TeamPerformanceReportForm } from './Reports/TeamPerformanceReportForm/TeamPerformanceReportForm';
import { TeamFixtureCardsReportForm } from './Reports/TeamFixtureCardsReportForm/TeamFixtureCardsReportForm';
import './SchoolTeams.css';
import '../Events/SchoolEventView/SchoolEventView.scss';
import { PeopleFilterChipSelect } from 'Src/views/GenericView/AdminView/Users/PeopleFilterChipSelect';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { TeamSeasonRolloverModal } from './TeamSeasonRolloverModal';
import { formatTeamAgesCompact, resolveAgeNamingFromContext } from 'Src/helpers/ageGroups';

const TEAM_COLUMN_VISIBILITY_KEY = 'sportsFixtures.teams';
const TEAM_OPTIONAL_COLUMNS = ['sport', 'gender', 'age', 'squad', 'house'];

const getCurrentAcademicYear = (): string => {
  const now = new Date();
  const start = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}/${String(start + 1).slice(-2)}`;
};

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Sport',
    field: 'sport',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['sport', 'name']
  },
  {
    text: 'Name',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Season',
    field: 'season',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['season']
  },
  {
    text: 'Description',
    field: 'description',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['description']
  },
  {
    text: 'Age',
    field: 'teamAges',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getEventAgesGroup
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['gender']
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name']
  }
];

const TEAM_ICON_PATHS = {
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.35-4.35',
  filter: 'M3 5h18M6 12h12M10 19h4',
  plus: 'M12 5v14M5 12h14',
  whistle: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z',
  users:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  more: 'M12 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2M12 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2',
  columns: 'M3 5h18v14H3zM9 5v14M15 5v14'
};

const TeamIcon = ({ name, size = 16 }: { name: keyof typeof TEAM_ICON_PATHS; size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.85"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={TEAM_ICON_PATHS[name]} />
  </svg>
);

interface State {
  items: Team[];
  currentPage: number;
  selectedItems: Team[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;

  currentTabIndex: number;
  isError: boolean;
  errorMessage: string;
  isAddTeamModalOpen: boolean;

  forms: SchoolForm[];
  sports: Sport[];
  houses: SchoolHouse[];
  kits: KitLabBackendKit[];
  players: any[];
  isEditTeamPlayers: boolean;
  isDeleteEventModalOpen: boolean;
  isSuccessDeleteEventModalOpen: boolean;
  isErrorModalOpen: boolean;
  isTeamPerformanceReportModalOpen: boolean; //027
  isTeamFixtureCardsReportModalOpen: boolean;

  isKitModalOpen: boolean;
  kitModalTeam: Team | null;
  isColumnsMenuOpen: boolean;
  hiddenTeamColumns: Set<string>;
  isTeamDrawerOpen: boolean;
  isReportsModalOpen: boolean;
  deleteConfirm: string;
  sportFilterSearch: string;
  isSportFilterOptionsOpen: boolean;
  seasonView: 'current' | 'archive';
  archiveSeason: string;
  isRolloverModalOpen: boolean;
  rolloverInitialTeam?: Team;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class SchoolTeams extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,

      currentTabIndex: 0,

      isTeamPerformanceReportModalOpen: false,
      isTeamFixtureCardsReportModalOpen: false,

      isError: false,
      errorMessage: '',
      isAddTeamModalOpen: false,

      forms: [],
      sports: [],
      houses: [],
      kits: [],
      players: [],
      isEditTeamPlayers: true,
      isErrorModalOpen: false,
      isDeleteEventModalOpen: false,
      isSuccessDeleteEventModalOpen: false,

      isKitModalOpen: false,
      kitModalTeam: null,
      isColumnsMenuOpen: false,
      hiddenTeamColumns: new Set<string>(
        loadHiddenColumns(props.user, TEAM_COLUMN_VISIBILITY_KEY).filter(column =>
          TEAM_OPTIONAL_COLUMNS.includes(column)
        )
      ),
      isTeamDrawerOpen: false,
      isReportsModalOpen: false,
      deleteConfirm: '',
      sportFilterSearch: '',
      isSportFilterOptionsOpen: false,
      seasonView: 'current',
      archiveSeason: `${Number(getCurrentAcademicYear().slice(0, 4)) - 1}/${String(
        Number(getCurrentAcademicYear().slice(0, 4))
      ).slice(-2)}`,
      isRolloverModalOpen: false,
      rolloverInitialTeam: undefined
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    document.addEventListener('mousedown', this.onColumnsOutsideClick);
    this.setAdditionalItems().then(res => {
      this.setItems();
    });
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onColumnsOutsideClick);
  }

  onColumnsOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (
      target &&
      target.closest &&
      (target.closest('.teamsPage__columns') ||
        target.closest('.teamsPage__filters') ||
        target.closest('.teamsPage__filterBtn'))
    ) {
      return;
    }
    if (this.state.isColumnsMenuOpen) this.setState({ isColumnsMenuOpen: false });
    if (this.state.isShowFilter) {
      this.setState({ isShowFilter: false });
      this.setItems();
    }
  };

  setAdditionalItems() {
    const { user } = this.props;

    const filter = {
      where: {
        players: {
          $nin: [TYPE_OF_PLAYERS['1X1'], TYPE_OF_PLAYERS.INDIVIDUAL]
        },
        isSchoolEventsAvailable: 'true'
      },
      limit: DEFAULT_LIMIT,
      order: 'name ASC',
      skip: DEFAULT_SKIP
    };
    const formsPromise = getAllForms(user);
    const sportsPromise = getSchoolSports(user, filter);
    const housesPromise = getAllHouses(user);
    const kitsPromise = getKitLabKits(user).catch(() => [] as KitLabBackendKit[]);

    return Promise.all([formsPromise, sportsPromise, housesPromise, kitsPromise]).then(
      ([forms, sports, houses, kits]) => {
        const allowedInSchoolSportIds = propz.get(user, ['activeSchool', 'allowedSportIds'], []);
        const isAllSportsAllowed = allowedInSchoolSportIds.length === 0;

        let sportsForFilter = isAllSportsAllowed
          ? sports
          : sports.filter(sport => {
              const isSportAllowedInSchool = allowedInSchoolSportIds.some(id => id === sport.id);
              return isSportAllowedInSchool;
            });

        this.setState({
          forms: forms,
          sports: sportsForFilter,
          houses,
          kits
        });

        return true;
      }
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const nextTeamId = propz.get(nextProps, ['location', 'state', 'teamId']);

    if (nextProps.location.search !== this.props.location.search || typeof nextTeamId !== 'undefined') {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps.location);
    }
  }

  setItems(targetLocation: Location = this.props.location) {
    const { history, user } = this.props;
    const location = targetLocation;

    const search = parse(location.search);

    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    const lastSelectedFilters = propz.get(location, ['state', 'selectedFilters']);
    const isExistSelectedFilters = typeof lastSelectedFilters !== 'undefined';

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);
    const selectedSportId = propz.get(filters, ['sport', 0]);
    const selectedSport = this.state.sports.find(sport => String(sport.id) === String(selectedSportId));
    const sportFilterSearch = selectedSport ? selectedSport.name : '';

    const isDataFiltered = isFilterExist2(filters);

    const where = {
      ...getServerFieldSectionWhere(filters),
      season: this.state.seasonView === 'current' ? getCurrentAcademicYear() : this.state.archiveSeason
    };

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'createdAt ASC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getSchoolTeams(user, serverQueryFilter);
    const getItemsCountPromise = getSchoolTeamsCount(user, where);

    return BPromise.all([getItemsCountPromise, getItemsPromise]).then(([countObj, items]) => {
      const teamId = propz.get(location, ['state', 'teamId']);

      if (typeof teamId !== 'undefined') {
        const team = items.find(item => item.id === teamId);
        this.setState({
          currentPage: page,
          sortDirection: sortDirection,
          sortColumnsName: sortColumnsName,
          isShowFilter: false,
          isDataFiltered: isDataFiltered,
          filters: filters,
          items: items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          selectedItems: typeof team !== 'undefined' ? [team] : [],
          isSelectAllChecked: false,
          isTeamDrawerOpen: typeof team !== 'undefined',
          sportFilterSearch,
          isSportFilterOptionsOpen: false
        });
        const state = { ...(location.state as any) };
        delete state.teamId;
        history.replace({ ...location, state });
      } else {
        this.setState({
          currentPage: page,
          sortDirection: sortDirection,
          sortColumnsName: sortColumnsName,
          isShowFilter: false,
          isDataFiltered: isDataFiltered,
          filters: filters,
          items: items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          sportFilterSearch,
          isSportFilterOptionsOpen: false
        });
      }

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number, openDrawer = true): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
      currentTabIndex: 0,
      isTeamDrawerOpen: openDrawer && selectedItemsUpdated.length === 1
    });
  };

  onOpenTeamClick = (index: number): void => {
    const team = this.state.items[index];
    if (!team) return;

    this.setState({
      selectedItems: [team],
      currentTabIndex: 0,
      isSelectAllChecked: false,
      isTeamDrawerOpen: true
    });
  };

  onToggleTeamColumn = (column: string): void => {
    const hiddenTeamColumns = new Set(this.state.hiddenTeamColumns);
    hiddenTeamColumns.has(column) ? hiddenTeamColumns.delete(column) : hiddenTeamColumns.add(column);
    saveHiddenColumns(this.props.user, TEAM_COLUMN_VISIBILITY_KEY, Array.from(hiddenTeamColumns));
    this.setState({ hiddenTeamColumns });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true,
      isTeamDrawerOpen: false
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    getAllSchoolTeams(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isTeamDrawerOpen: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected && options[i].value !== '') {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onChipFilterToggle = (filterField: 'sport' | 'gender' | 'teamAges' | 'houses', value: string): void => {
    const currentValues = Array.isArray(this.state.filters[filterField]) ? this.state.filters[filterField] : [];
    const nextValues = currentValues.includes(value)
      ? currentValues.filter(currentValue => currentValue !== value)
      : [...currentValues, value];

    this.setState({
      filters: {
        ...this.state.filters,
        [filterField]: nextValues
      }
    });
  };

  onSportFilterSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const sportFilterSearch = event.target.value;
    const selectedSport = this.state.sports.find(
      sport => sport.name.toLocaleLowerCase() === sportFilterSearch.trim().toLocaleLowerCase()
    );

    this.setState({
      sportFilterSearch,
      isSportFilterOptionsOpen: true,
      filters: {
        ...this.state.filters,
        sport: selectedSport ? [selectedSport.id] : []
      }
    });
  };

  onSportFilterSelect = (sport: Sport): void => {
    this.setState({
      sportFilterSearch: sport.name,
      isSportFilterOptionsOpen: false,
      filters: {
        ...this.state.filters,
        sport: [sport.id]
      }
    });
  };

  onSportFilterClear = (): void => {
    this.setState({
      sportFilterSearch: '',
      isSportFilterOptionsOpen: false,
      filters: {
        ...this.state.filters,
        sport: []
      }
    });
  };

  onSportFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    const search = this.state.sportFilterSearch.trim().toLocaleLowerCase();
    const matchingSports = this.state.sports.filter(sport => sport.name.toLocaleLowerCase().includes(search));
    const exactSport = matchingSports.find(sport => sport.name.toLocaleLowerCase() === search);
    const selectedSport = exactSport || (matchingSports.length === 1 ? matchingSports[0] : undefined);

    if (selectedSport) {
      this.setState(
        {
          sportFilterSearch: selectedSport.name,
          isSportFilterOptionsOpen: false,
          filters: {
            ...this.state.filters,
            sport: [selectedSport.id]
          }
        },
        this.onApplyFilterClick
      );
      return;
    }

    this.setState({ isSportFilterOptionsOpen: true });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      filters: {},
      sportFilterSearch: '',
      isSportFilterOptionsOpen: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    if (isShowFilter) {
      this.setState({ isShowFilter: false, isSportFilterOptionsOpen: false }, () => this.setItems());
      return;
    }

    this.setState({
      isShowFilter: true,
      isColumnsMenuOpen: false,
      isSportFilterOptionsOpen: false
    });
  };

  onTabClick = (event, index: number) => {
    event.preventDefault();
    const { user } = this.props;
    const { selectedItems } = this.state;
    const team = selectedItems[0];
    const tabs = TEAM_TABS;
    const currentTab = tabs[index];
    this.setState({
      isEditTeamPlayers: true
    });
    switch (currentTab) {
      case TABS.PARTICIPANTS:
        this.setState({
          isLoading: true
        });
        getAllSchoolTeamPlayers(user, team.id).then(players => {
          this.setState({
            currentTabIndex: index,
            players: players,
            isLoading: false
          });
        });
        break;
      case TABS.KIT:
        this.setState({
          currentTabIndex: index
        });
        break;
      default:
        this.setState({
          currentTabIndex: index
        });
    }
  };

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  onAddTeamClick = () => {
    this.setState({
      selectedItems: [],
      isAddTeamModalOpen: true
    });
  };

  onEditTeamClick = () => {
    this.setState({
      isAddTeamModalOpen: true
    });
  };

  onCopyTeamClick = () => {
    this.setState({ isRolloverModalOpen: true, rolloverInitialTeam: this.state.selectedItems[0] });
  };

  onSetUpSeasonClick = () => {
    this.setState({ isRolloverModalOpen: true, rolloverInitialTeam: undefined });
  };

  onRolloverComplete = () => {
    this.setState(
      { isRolloverModalOpen: false, rolloverInitialTeam: undefined, seasonView: 'current', isLoading: true },
      () => this.setItems()
    );
  };

  onCloseSchoolTeamFormCancel = () => {
    this.setState({
      isAddTeamModalOpen: false
    });
  };

  onEditTeamMembersClick = () => {
    const { history } = this.props;
    const { selectedItems } = this.state;
    const team = selectedItems[0];
    const { id } = team;
    const search = history.location.search;

    history.push({
      pathname: `/teams/teamPlayers`,
      search: `team=${id}&mode=roster`,
      state: {
        search
      }
    });
  };

  onEditSportAssignmentsClick = () => {
    const { history } = this.props;
    const team = this.state.selectedItems[0];
    history.push({
      pathname: `/teams/teamPlayers`,
      search: `team=${team.id}&mode=assignments`,
      state: { search: history.location.search }
    });
  };

  onEditTeamPlayersClick = () => {
    const { isEditTeamPlayers, selectedItems, players, currentTabIndex } = this.state;
    const { user } = this.props;
    const team = selectedItems[0];
    const { id } = team;

    if (isEditTeamPlayers) {
      this.setState({
        isEditTeamPlayers: false
      });
    } else {
      this.setState({
        isLoading: true,
        isEditTeamPlayers: true
      });

      const data = {
        update: players.map(player => {
          const { isCaptain, isViceCaptain, sub, positionId, id } = player;
          return {
            isCaptain: isCaptain,
            isViceCaptain: isViceCaptain,
            sub: sub,
            positionId: positionId !== '' ? positionId : undefined,
            id: id
          };
        })
      };
      updateSchoolTeamPlayersBatch(user, id, data)
        .then(res => {
          this.setState({
            currentTabIndex: currentTabIndex,
            selectedItems: selectedItems
          });
          this.setItems();
        })
        .catch(error => {
          const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
          console.error(error);
          this.setState({
            isLoading: false,
            errorMessage: errorText,
            isError: true
          });
        });
    }
  };

  onChangePositionPlayer = (event, index) => {
    const positionValue = event.target.value;
    const players = [...this.state.players];

    const changePlayer = players[index];
    changePlayer.positionId = positionValue;

    players.splice(index, 1, changePlayer);

    this.setState({
      players: players
    });
  };

  onChangeIsSubPlayer = (event, index) => {
    const isSub = event.target.checked;
    const players = [...this.state.players];
    const changePlayer = players[index];
    changePlayer.sub = isSub;
    players.splice(index, 1, changePlayer);

    this.setState({
      players: players
    });
  };

  onChangeIsCaptainPlayer = (event, playerId) => {
    const isCaptain = event.target.checked;
    const players = [...this.state.players];
    const playerIndex = players.findIndex(player => player.id === playerId);
    const oldCaptainIndex = players.findIndex(player => player.isCaptain);
    players[playerIndex].isCaptain = isCaptain;

    if (isCaptain && oldCaptainIndex !== -1) {
      players[oldCaptainIndex].isCaptain = false;
    }
    if (isCaptain) players[playerIndex].isViceCaptain = false;

    this.setState({
      players: players
    });
  };

  onChangeIsViceCaptainPlayer = (event, playerId) => {
    const isViceCaptain = event.target.checked;
    const players = [...this.state.players];
    const playerIndex = players.findIndex(player => player.id === playerId);
    if (playerIndex === -1) return;
    players[playerIndex] = {
      ...players[playerIndex],
      isViceCaptain,
      ...(isViceCaptain ? { isCaptain: false } : {})
    };
    this.setState({ players });
  };

  openKitModal = () => {
    const { selectedItems } = this.state;
    if (selectedItems.length !== 1) return;

    this.setState({
      isKitModalOpen: true,
      kitModalTeam: selectedItems[0]
    });
  };

  closeKitModal = () => {
    this.setState({
      isKitModalOpen: false,
      kitModalTeam: null
    });
  };

  onKitSelected = (kit: KitLabBackendKit) => {
    const { user } = this.props;
    const { kitModalTeam } = this.state;
    if (!kitModalTeam) return;

    const teamId = kitModalTeam.id;
    const dataToSubmit = {
      kitLabKitId: kit._id
    };

    this.setState({ isLoading: true });

    updateSchoolTeam(user, teamId, dataToSubmit)
      .then(updatedTeam => {
        this.setState({
          isLoading: false,
          isKitModalOpen: false,
          kitModalTeam: null,
          selectedItems: [updatedTeam]
        });
        this.setItems();
      })
      .catch(error => {
        const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
        console.error(error);
        this.setState({
          isLoading: false,
          errorMessage: errorText,
          isError: true,
          isKitModalOpen: false,
          kitModalTeam: null
        });
      });
  };

  onKitCleared = () => {
    const { user } = this.props;
    const { kitModalTeam } = this.state;
    if (!kitModalTeam) return;

    const teamId = kitModalTeam.id;
    const dataToSubmit = {
      kitLabKitId: null
    };

    this.setState({ isLoading: true });

    updateSchoolTeam(user, teamId, dataToSubmit)
      .then(updatedTeam => {
        this.setState({
          isLoading: false,
          isKitModalOpen: false,
          kitModalTeam: null,
          selectedItems: [updatedTeam]
        });
        this.setItems();
      })
      .catch(error => {
        const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
        console.error(error);
        this.setState({
          isLoading: false,
          errorMessage: errorText,
          isError: true,
          isKitModalOpen: false,
          kitModalTeam: null
        });
      });
  };

  getSummaryTabs(): React.ReactNode {
    const { selectedItems, currentTabIndex, sports, players } = this.state;
    const { user } = this.props;
    const team = selectedItems[0];
    const teamSportIds = ((team as any).sportIds || [(team as any).sportId]).filter(Boolean).map(String);
    const teamSports = (team as any).sports || sports.filter(sport => teamSportIds.includes(String(sport.id)));

    switch (currentTabIndex) {
      case 0:
        return <SchoolTeamSummary team={team} user={this.props.user} />;
      case 1:
        return <SchoolTeamPlayersSummary players={players} sports={teamSports} />;
      case 2:
        return (
          <SchoolTeamKitSummary
            user={user}
            team={team}
            onChooseKit={this.openKitModal}
            onClearKit={() => {
              this.setState({ kitModalTeam: team }, () => this.onKitCleared());
            }}
          />
        );
    }
  }

  onTeamFormSubmit = data => {
    const { user } = this.props;
    const { selectedItems } = this.state;
    const { sport, sports, ages, house, isHouseFilter, houseId, ...rest } = data;
    const agesNumber = ages.map(age => Number(age));

    const dataToSubmit = isHouseFilter
      ? {
          ...rest,
          houseId: houseId,
          ages: agesNumber,
          schoolId: user.activeSchoolId
        }
      : {
          ...rest,
          ages: agesNumber,
          schoolId: user.activeSchoolId
        };

    this.setState({
      isLoading: true,
      isAddTeamModalOpen: false
    });

    switch (selectedItems.length) {
      case 0: {
        createSchoolTeam(user, dataToSubmit)
          .then(team => {
            const teamSeason = team.season || dataToSubmit.season || getCurrentAcademicYear();
            return getSchoolTeamsCount(user, { season: teamSeason }).then(countObj => {
              const lastPage = Math.max(FIRST_PAGE, Math.ceil(countObj.count / LIMIT));
              const pathname = this.state.basePath || this.props.location.pathname;
              const isCurrentSeason = teamSeason === getCurrentAcademicYear();

              this.setState({
                selectedItems: [],
                currentTabIndex: 0,
                players: [],
                isSelectAllChecked: false,
                isTeamDrawerOpen: false,
                isLoading: true,
                seasonView: isCurrentSeason ? 'current' : 'archive',
                archiveSeason: isCurrentSeason ? this.state.archiveSeason : teamSeason
              });
              this.props.history.push({
                pathname,
                search: lastPage > FIRST_PAGE ? `page=${lastPage}` : '',
                state: { teamId: team.id }
              });
            });
          })
          .catch(error => {
            const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
            console.error(error);
            this.setState({
              isLoading: false,
              errorMessage: errorText,
              isError: true
            });
          });
        break;
      }
      case 1:
        const teamId = selectedItems[0].id;
        updateSchoolTeam(user, teamId, dataToSubmit)
          .then(team => {
            this.setState({
              selectedItems: [team],
              currentTabIndex: 0,
              players: []
            });
            this.setItems();
          })
          .catch(error => {
            const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
            console.error(error);
            this.setState({
              isLoading: false,
              errorMessage: errorText,
              isError: true
            });
          });
        break;
    }
  };

  onSubmitReportForm = (data, reportType) => {
    const { user } = this.props;
    const { activeSchoolId, activeSchool } = user;
    const { selectedItems, forms } = this.state;
    const team = selectedItems[0];
    const teamId = propz.get(team, ['id'], '');

    const { dateTo, dateFrom, isShowUnclosed, includeCancelled } = data;

    let modData;
    let formsUniq;
    let ages: number[];

    switch (reportType) {
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TEAM_PERFORMANCE:
        this.setState({
          isTeamPerformanceReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);
        const formattedDateTo = moment(dateTo).format('YYYY-MM-DD HH:mm:ss');
        const formattedDateFrom = moment(dateFrom).format('YYYY-MM-DD HH:mm:ss');

        modData = {
          reportParamsCSV: {
            schoolId: activeSchoolId,
            teamId,
            dateFrom: formattedDateFrom,
            dateTo: formattedDateTo,
            showUnclosed: isShowUnclosed
          }
        };
        break;
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TEAM_FIXTURE_CARDS:
        this.setState({
          isTeamFixtureCardsReportModalOpen: false,
          isLoading: true
        });
        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 999);
        modData = {
          reportParamsCSV: {
            schoolId: activeSchoolId,
            teamId,
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            includeCancelled
          }
        };
        break;
    }

    uploadReport(user, reportType, modData)
      .then(data => {
        if (typeof data.reportId !== 'undefined') {
          const { reportType, fileType } = data;

          this.setState({
            isLoading: false
          });

          const fileTypePath = fileType ? `/fileType/${fileType}` : '';
          const url = `${window.apiBase}/public/report/${data.reportId}/reportType/${reportType}${fileTypePath}`;
          window.open(url);
        } else {
          this.setState({
            isLoading: false,
            errorMessage: 'No read report',
            isErrorModalOpen: true
          });
        }
      })
      .catch(error => {
        const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
        console.error(error);

        this.setState({
          isLoading: false,
          errorMessage: errorText,
          isErrorModalOpen: true
        });
      });
  };

  openTeamPerformanceReportForm = () => {
    this.setState({
      isTeamPerformanceReportModalOpen: true,
      isReportsModalOpen: false
    });
  };

  closeTeamPerformanceReportForm = () => {
    this.setState({
      isTeamPerformanceReportModalOpen: false
    });
  };

  openTeamFixtureCardsReportForm = () => {
    this.setState({ isTeamFixtureCardsReportModalOpen: true, isReportsModalOpen: false });
  };

  closeTeamFixtureCardsReportForm = () => {
    this.setState({ isTeamFixtureCardsReportModalOpen: false });
  };

  openReportsModal = () => this.setState({ isReportsModalOpen: true });
  closeReportsModal = () => this.setState({ isReportsModalOpen: false });

  renderReportsModal() {
    const { isReportsModalOpen, selectedItems } = this.state;
    const isSingleTeam = selectedItems.length === 1;
    return (
      <SimpleModal
        customClass="eEventPrintModal"
        isOpen={isReportsModalOpen}
        title="Team reports"
        onCloseClick={this.closeReportsModal}
        showFooter={false}
        isBodyBold={false}
      >
        <div className="eEventPrintModal__intro">
          Choose a report. You can configure its period and contents on the next step.
        </div>
        <section className="eEventPrintModal__group">
          <h3 className="eEventPrintModal__groupTitle">Performance & fixtures</h3>
          <div className="eEventPrintModal__list">
            <button
              type="button"
              className="eEventPrintModal__item"
              disabled={!isSingleTeam}
              onClick={this.openTeamPerformanceReportForm}
            >
              <span className="eEventPrintModal__itemIcon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z" />
                </svg>
              </span>
              <span className="eEventPrintModal__itemCopy">
                <span className="eEventPrintModal__itemHeading">
                  <span>Team performance/schedule</span>
                  <span className="eEventPrintModal__format">PDF</span>
                </span>
                <span className="eEventPrintModal__itemDescription">
                  {isSingleTeam
                    ? 'Review results and upcoming fixtures for the selected team.'
                    : 'Select one team to generate this report.'}
                </span>
              </span>
              <span className="eEventPrintModal__arrow" aria-hidden="true">
                ›
              </span>
            </button>
            <button
              type="button"
              className="eEventPrintModal__item"
              disabled={!isSingleTeam}
              onClick={this.openTeamFixtureCardsReportForm}
            >
              <span className="eEventPrintModal__itemIcon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M4 5h16v14H4zM4 10h16M9 5v5M15 5v5" />
                </svg>
              </span>
              <span className="eEventPrintModal__itemCopy">
                <span className="eEventPrintModal__itemHeading">
                  <span>Fixture cards</span>
                  <span className="eEventPrintModal__format">PDF</span>
                </span>
                <span className="eEventPrintModal__itemDescription">
                  {isSingleTeam
                    ? 'Create printable cards for every fixture involving the selected team.'
                    : 'Select one team to generate fixture cards.'}
                </span>
              </span>
              <span className="eEventPrintModal__arrow" aria-hidden="true">
                ›
              </span>
            </button>
          </div>
        </section>
      </SimpleModal>
    );
  }

  renderTeamPerformanceReportFormModal() {
    const { isTeamPerformanceReportModalOpen, selectedItems } = this.state;
    const team = selectedItems[0];
    return (
      <SimpleModal
        isOpen={isTeamPerformanceReportModalOpen}
        title="Team performance/schedule"
        onCloseClick={this.closeTeamPerformanceReportForm}
        showFooter={false}
        isBodyBold={false}
        customClass="eEventReportParametersModal"
        bodyClassName="eEventReportParametersModal__body"
      >
        <TeamPerformanceReportForm
          onCloseClick={this.closeTeamPerformanceReportForm}
          onSubmit={this.onSubmitReportForm}
          team={team}
        />
      </SimpleModal>
    );
  }

  renderTeamFixtureCardsReportFormModal() {
    const { isTeamFixtureCardsReportModalOpen, selectedItems } = this.state;
    return (
      <SimpleModal
        isOpen={isTeamFixtureCardsReportModalOpen}
        title="Fixture cards"
        onCloseClick={this.closeTeamFixtureCardsReportForm}
        showFooter={false}
        isBodyBold={false}
        customClass="eEventReportParametersModal"
        bodyClassName="eEventReportParametersModal__body"
      >
        <TeamFixtureCardsReportForm
          onCloseClick={this.closeTeamFixtureCardsReportForm}
          onSubmit={this.onSubmitReportForm}
          team={selectedItems[0]}
        />
      </SimpleModal>
    );
  }

  renderTeamForm() {
    const { isAddTeamModalOpen, selectedItems, forms, houses } = this.state;
    const { user } = this.props;

    if (selectedItems.length === 0) {
      return (
        <SimpleModal
          isOpen={isAddTeamModalOpen}
          title={'Create team'}
          showFooter={false}
          onCloseClick={this.onCloseSchoolTeamFormCancel}
          guardUnsavedChanges
          customClass="teamFormModal"
          isBodyBold={false}
        >
          <SchoolTeamForm
            onSubmit={this.onTeamFormSubmit}
            onCancel={this.onCloseSchoolTeamFormCancel}
            user={user}
            forms={forms}
            houses={houses}
          />
        </SimpleModal>
      );
    } else {
      const team = selectedItems[0];
      return (
        <SimpleModal
          isOpen={isAddTeamModalOpen}
          title={'Edit team'}
          showFooter={false}
          onCloseClick={this.onCloseSchoolTeamFormCancel}
          guardUnsavedChanges
          customClass="teamFormModal"
          isBodyBold={false}
        >
          <SchoolTeamForm
            onSubmit={this.onTeamFormSubmit}
            onCancel={this.onCloseSchoolTeamFormCancel}
            user={user}
            team={team}
            forms={forms}
            houses={houses}
          />
        </SimpleModal>
      );
    }
  }

  onCloseErrorClick = (event: Event) => {
    event.preventDefault();

    this.setState({
      isError: false,
      errorMessage: ''
    });
  };

  onDeleteTeamClick = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isDeleteEventModalOpen: false,
      isLoading: true
    });

    const promises = selectedItems.map(event => {
      return deleteTeam(user, event.id);
    });
    BPromise.all(promises)
      .then(() => {
        this.setState({
          isSelectAllChecked: false,
          selectedItems: [],
          isTeamDrawerOpen: false
        });
        return this.setItems();
      })
      .catch(error => {
        const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
        console.error(error);
        this.setState({
          isLoading: false,
          errorMessage: errorText,
          isErrorModalOpen: true
        });
      });
  };

  deleteTeamModalOpen = (): void => {
    this.setState({
      isDeleteEventModalOpen: true,
      deleteConfirm: ''
    });
  };

  onCloseDeleteTeamConfirmationModalClick = (): void => {
    this.setState({
      isDeleteEventModalOpen: false
    });
  };

  renderDeleteTeamConfirmationModal(): React.ReactNode {
    const { isDeleteEventModalOpen, deleteConfirm } = this.state;
    const { selectedItems: teams } = this.state;
    const isConfirmed = deleteConfirm.trim().toUpperCase() === 'DELETE';

    return (
      <SimpleModal
        isOpen={isDeleteEventModalOpen}
        title="Delete teams"
        onCloseClick={this.onCloseDeleteTeamConfirmationModalClick}
        customClass="teamsDeleteModal"
        dialogStyle={{ maxWidth: 560 }}
        showFooter={false}
        isBodyBold={false}
      >
        <div className="teamsDelete">
          <div className="teamsDelete__alert">
            <span className="teamsDelete__icon">!</span>
            <div>
              <h3>This action cannot be undone.</h3>
              <p>
                You are about to permanently remove {teams.length} {teams.length === 1 ? 'team' : 'teams'}.
              </p>
            </div>
          </div>
          <div className="teamsDelete__details">
            <p>Deleting teams removes their squad setup and may affect events, reports and linked records.</p>
            <p className="teamsDelete__names">Selected: {teams.map(team => team.name).join(', ')}</p>
          </div>
          <label className="teamsDelete__confirm">
            <span>
              Type <strong>DELETE</strong> to confirm.
            </span>
            <input
              value={deleteConfirm}
              placeholder="DELETE"
              autoFocus
              onChange={event => this.setState({ deleteConfirm: event.target.value })}
              onKeyDown={event => event.key === 'Enter' && isConfirmed && this.onDeleteTeamClick()}
            />
          </label>
          <div className="teamsDelete__footer">
            <button
              type="button"
              className="teamsDelete__cancel"
              onClick={this.onCloseDeleteTeamConfirmationModalClick}
            >
              Cancel
            </button>
            <button
              type="button"
              className="teamsDelete__submit"
              disabled={!isConfirmed}
              onClick={this.onDeleteTeamClick}
            >
              Delete {teams.length} {teams.length === 1 ? 'team' : 'teams'}
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  renderSummary() {
    const { selectedItems, currentTabIndex, isEditTeamPlayers, players } = this.state;
    const isOneItemSelected = selectedItems.length === 1;

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a team above</div>;
    }

    const summaryTabs = this.getSummaryTabs();

    const isShowEditTeamButton = currentTabIndex === 0;
    const isShowEditTeamMembersButton = currentTabIndex === 1;

    const editTeamPlayersButtonText = isEditTeamPlayers ? 'Edit positions' : 'Save positions';
    const isPlayersExist = players.length > 0;

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={TEAM_TABS} currentTabIndex={currentTabIndex}>
        {isShowEditTeamButton && (
          <Button onClick={this.onEditTeamClick} text={'Edit details'} customClass={'mt-3 mb-3'} />
        )}
        {isShowEditTeamMembersButton && (
          <>
            <Button onClick={this.onEditTeamMembersClick} text={'Manage roster'} customClass={'mt-3 mb-3'} />
            <Button
              onClick={this.onEditSportAssignmentsClick}
              text={'Edit sport assignments'}
              customClass={'mt-3 mb-3 ml-3'}
              disabled={!isPlayersExist}
            />
          </>
        )}
        {summaryTabs}
      </GenericSummary>
    );
  }

  renderKitModal() {
    const { isKitModalOpen, kitModalTeam } = this.state;
    const { user } = this.props;

    if (!isKitModalOpen || !kitModalTeam) return null;

    return (
      <SimpleModal
        isOpen={isKitModalOpen}
        title={`Choose kit for ${kitModalTeam.name}`}
        onCloseClick={this.closeKitModal}
        customClass="eKitSelectorModal eKitSelectorModal--compact"
        dialogStyle={{ maxWidth: 680 }}
        zIndex={1200}
        showFooter={false}
      >
        <KitSelector
          user={user}
          team={kitModalTeam}
          onSelect={this.onKitSelected}
          onClear={this.onKitCleared}
          onCancel={this.closeKitModal}
        />
      </SimpleModal>
    );
  }

  getTeamAgesLabel(team: Team): string {
    const teamAges = Array.from(
      new Set(
        (Array.isArray((team as any).ages) ? (team as any).ages : [])
          .map(age => Number(age))
          .filter(age => Number.isFinite(age))
      )
    );
    const availableAges = Array.from(
      new Set(this.state.forms.map(form => Number(form.age)).filter(age => Number.isFinite(age)))
    );
    const includesAllAvailableAges = availableAges.length > 0 && availableAges.every(age => teamAges.includes(age));

    if (teamAges.length === 0 || includesAllAvailableAges) {
      return 'All years';
    }

    return formatTeamAgesCompact(teamAges, resolveAgeNamingFromContext(this.props.user)) || 'All years';
  }

  renderTeamKitThumbnail(team: Team): React.ReactNode {
    const kitId = String((team as any).kitLabKitId || '');
    const kit = this.state.kits.find(item => String(item._id) === kitId);

    if (!kit) {
      return <TeamIcon name="whistle" size={18} />;
    }

    const layout = getLayoutForKit(kit);
    const pocketColor = resolveDetailColor(
      layout.pocketColorSource,
      layout.pocketCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const sleevesColor = resolveDetailColor(
      layout.sleevesColorSource,
      layout.sleevesCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const collarColor = resolveDetailColor(
      layout.collarColorSource,
      layout.collarCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const shoulderTrianglesColor = resolveDetailColor(
      layout.shoulderTrianglesColorSource,
      layout.shoulderTrianglesCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const sleeveCuffsColor = resolveDetailColor(
      layout.sleeveCuffsColorSource,
      layout.sleeveCuffsCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );

    return (
      <KitShirtPreview
        variant={layout.shirtPattern}
        primaryColor={layout.primaryColor}
        secondaryColor={layout.secondaryColor}
        showPocket={layout.showPocket}
        showCollar={layout.showCollar}
        showSleeves={layout.showSleeves}
        showShoulderTriangles={layout.showShoulderTriangles}
        showSleeveCuffs={layout.showSleeveCuffs}
        pocketColor={pocketColor}
        sleevesColor={sleevesColor}
        collarColor={collarColor}
        shoulderTrianglesColor={shoulderTrianglesColor}
        sleeveCuffsColor={sleeveCuffsColor}
        stripeCount={layout.stripeCount}
        stripesEqualWidth={layout.stripesEqualWidth}
        stripePattern={layout.stripePattern}
        diagonalDirection={layout.diagonalDirection}
        chevronSize={layout.chevronSize}
        chevronDirection={layout.chevronDirection}
        chevronPosition={layout.chevronPosition}
        sideTrianglesMode={layout.sideTrianglesMode}
        sleevePattern={layout.sleevePattern}
        collarStyle={layout.collarStyle}
        className="teamsPage__kitThumbnailSvg"
      />
    );
  }

  renderGrid() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      selectedItems,
      filters,
      currentPage,
      total,
      forms,
      sports,
      houses,
      isColumnsMenuOpen,
      hiddenTeamColumns
    } = this.state;

    const { user } = this.props;

    const activeSchool = user.activeSchool;
    const ageGroupsNaming = activeSchool.ageGroupsNaming;
    const sportOptions = getSelectOptionForSport(sports);
    const ageOptions = getSelectOptionForAge(forms, ageGroupsNaming);
    const genderOptions = getSelectOptionForTournamentTeamGender();
    const houseOptions = getSelectOptionForHouses(houses);
    const selectedIds = new Set(selectedItems.map(team => team.id));
    const pageCount = Math.max(1, Math.ceil(total / LIMIT));
    const pageStart = Math.max(1, Math.min(currentPage - 2, pageCount - 4));
    const pageEnd = Math.min(pageCount, pageStart + 4);
    const pages = Array.from({ length: pageEnd - pageStart + 1 }, (_, index) => pageStart + index);
    const sortMark = field => (sortColumnsName === field ? (sortDirection === 'ASC' ? '↑' : '↓') : '↕');
    const nameFilter = filters.name || '';
    const normalizedSportSearch = this.state.sportFilterSearch.trim().toLocaleLowerCase();
    const sportSearchOptions = sports
      .filter(sport => !normalizedSportSearch || sport.name.toLocaleLowerCase().includes(normalizedSportSearch))
      .slice(0, 12);
    const drawerFilters = [
      { field: 'sport', label: 'Sport', options: sportOptions },
      { field: 'gender', label: 'Gender', options: genderOptions },
      { field: 'teamAges', label: 'Age', options: ageOptions },
      { field: 'houses', label: 'House', options: houseOptions }
    ];
    const activeDrawerFilters = drawerFilters.filter(filter => (filters[filter.field] || []).length > 0);
    const hasActiveFilters = Boolean(String(nameFilter).trim()) || activeDrawerFilters.length > 0;
    const visibleColumnCount = 4 + TEAM_OPTIONAL_COLUMNS.length - hiddenTeamColumns.size;
    const currentStartYear = Number(getCurrentAcademicYear().slice(0, 4));
    const archiveSeasons = Array.from({ length: 12 }, (_, index) => {
      const start = currentStartYear - index - 1;
      return `${start}/${String(start + 1).slice(-2)}`;
    });

    return (
      <div className="teamsPage__list">
        <header className="teamsPage__header">
          <div className="teamsPage__headerTitle">
            <h1>Teams</h1>
            <span>{total}</span>
          </div>
          <div className="teamsPage__headerActions">
            <button type="button" className="teamsBtn" onClick={this.onSetUpSeasonClick}>
              Set up teams for {getCurrentAcademicYear()}
            </button>
            <button type="button" className="teamsBtn teamsBtn--primary" onClick={this.onAddTeamClick}>
              <TeamIcon name="plus" /> Add team
            </button>
          </div>
        </header>

        <div className="teamsPage__seasonNav">
          <button
            type="button"
            className={this.state.seasonView === 'current' ? 'is-active' : ''}
            onClick={() =>
              this.setState(
                {
                  seasonView: 'current',
                  currentPage: FIRST_PAGE,
                  isLoading: true,
                  selectedItems: [],
                  isSelectAllChecked: false,
                  isTeamDrawerOpen: false
                },
                () => {
                  const location = { ...this.props.location, search: '' } as Location;
                  this.props.history.push(location);
                  this.setItems(location);
                }
              )
            }
          >
            Current season · {getCurrentAcademicYear()}
          </button>
          <button
            type="button"
            className={this.state.seasonView === 'archive' ? 'is-active' : ''}
            onClick={() =>
              this.setState(
                {
                  seasonView: 'archive',
                  currentPage: FIRST_PAGE,
                  isLoading: true,
                  selectedItems: [],
                  isSelectAllChecked: false,
                  isTeamDrawerOpen: false
                },
                () => {
                  const location = { ...this.props.location, search: '' } as Location;
                  this.props.history.push(location);
                  this.setItems(location);
                }
              )
            }
          >
            Archive
          </button>
          {this.state.seasonView === 'archive' && (
            <select
              aria-label="Archive season"
              value={this.state.archiveSeason}
              onChange={event =>
                this.setState(
                  {
                    archiveSeason: event.target.value,
                    currentPage: FIRST_PAGE,
                    isLoading: true,
                    selectedItems: [],
                    isSelectAllChecked: false,
                    isTeamDrawerOpen: false
                  },
                  () => {
                    const location = { ...this.props.location, search: '' } as Location;
                    this.props.history.push(location);
                    this.setItems(location);
                  }
                )
              }
            >
              {archiveSeasons.map(season => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>
          )}
        </div>

        <section className="teamsPage__tableCard">
          <div className="teamsPage__toolbar">
            <label className="teamsPage__search">
              <input
                value={nameFilter}
                placeholder="Search team names…"
                onChange={event => this.onTableFilterChange(event, 'name')}
                onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
              />
              {nameFilter ? (
                <button
                  type="button"
                  aria-label="Clear team search"
                  onClick={() =>
                    this.setState({ filters: { ...this.state.filters, name: '' } }, this.onApplyFilterClick)
                  }
                >
                  ×
                </button>
              ) : null}
            </label>
            <button
              type="button"
              className={`teamsPage__filterBtn ${isShowFilter ? 'is-active' : ''}`}
              onClick={this.onTableFilterClick}
              aria-label="Filters"
            >
              <span className="teamsPage__toolbarIcon teamsPage__toolbarIcon--filter" />
              Filters
              {activeDrawerFilters.length > 0 && (
                <span className="teamsPage__filterBadge">{activeDrawerFilters.length}</span>
              )}
            </button>
            <div className="teamsPage__columns">
              <button
                type="button"
                className={`teamsPage__columnsBtn ${isColumnsMenuOpen ? 'is-active' : ''}`}
                aria-label="Choose columns"
                aria-expanded={isColumnsMenuOpen}
                onClick={() => this.setState({ isColumnsMenuOpen: !isColumnsMenuOpen })}
              >
                <span className="teamsPage__toolbarIcon teamsPage__toolbarIcon--columns" />
                Columns <span className="teamsPage__columnsBadge">{5 - hiddenTeamColumns.size}</span>
              </button>
              {isColumnsMenuOpen && (
                <div className="teamsPage__columnsMenu">
                  <strong>Columns</strong>
                  {[
                    ['sport', 'Sport'],
                    ['gender', 'Gender'],
                    ['age', 'Age'],
                    ['squad', 'Squad'],
                    ['house', 'House']
                  ].map(([key, label]) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        checked={!hiddenTeamColumns.has(key)}
                        onChange={() => this.onToggleTeamColumn(key)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          {hasActiveFilters && (
            <div className="teamsPage__filterChips">
              {nameFilter ? (
                <span>
                  <b>Search:</b> {nameFilter}
                  <button
                    type="button"
                    aria-label="Remove search filter"
                    onClick={() =>
                      this.setState({ filters: { ...this.state.filters, name: '' } }, this.onApplyFilterClick)
                    }
                  >
                    ×
                  </button>
                </span>
              ) : null}
              {activeDrawerFilters.map(filter => {
                const values = filters[filter.field] || [];
                const labels = values.map(value => {
                  const option = filter.options.find(item => String(item.value) === String(value));
                  return option ? option.text : value;
                });
                return (
                  <span key={filter.field}>
                    <b>{filter.label}:</b> {labels.join(', ')}
                    <button
                      type="button"
                      aria-label={`Remove ${filter.label} filter`}
                      onClick={() => {
                        const nextState: any = { filters: { ...this.state.filters, [filter.field]: [] } };
                        if (filter.field === 'sport') nextState.sportFilterSearch = '';
                        this.setState(nextState, this.onApplyFilterClick);
                      }}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
              <button type="button" className="teamsPage__filterChipsClear" onClick={this.onClearFilterClick}>
                Clear all
              </button>
            </div>
          )}
          {isShowFilter && (
            <div className="teamsPage__filters">
              <div className="teamsPage__filtersHeader">
                <div>
                  <strong>Filters</strong>
                </div>
                <button type="button" aria-label="Close filters" onClick={this.onTableFilterClick}>
                  ×
                </button>
              </div>
              <label className="teamsPage__sportFilter">
                <span>Sport</span>
                <div className="teamsPage__sportFilterControl">
                  <input
                    type="text"
                    value={this.state.sportFilterSearch}
                    placeholder="Search by sport"
                    autoComplete="off"
                    onFocus={() => this.setState({ isSportFilterOptionsOpen: true })}
                    onChange={this.onSportFilterSearchChange}
                    onKeyDown={this.onSportFilterKeyDown}
                  />
                  {this.state.sportFilterSearch ? (
                    <button type="button" aria-label="Clear sport filter" onClick={this.onSportFilterClear}>
                      ×
                    </button>
                  ) : null}
                  {this.state.isSportFilterOptionsOpen && (
                    <div className="teamsPage__sportFilterOptions" role="listbox" aria-label="Sports">
                      {sportSearchOptions.length > 0 ? (
                        sportSearchOptions.map(sport => (
                          <button
                            type="button"
                            role="option"
                            aria-selected={(filters.sport || []).map(String).includes(String(sport.id))}
                            key={sport.id}
                            onClick={() => this.onSportFilterSelect(sport)}
                          >
                            {sport.name}
                          </button>
                        ))
                      ) : (
                        <span>No sports found</span>
                      )}
                    </div>
                  )}
                </div>
              </label>
              <PeopleFilterChipSelect
                label="Gender"
                options={genderOptions.map(option => ({ value: String(option.value), label: String(option.text) }))}
                selected={(filters.gender || []).map(String)}
                onToggle={value => this.onChipFilterToggle('gender', value)}
              />
              <PeopleFilterChipSelect
                label="Age"
                options={ageOptions.map((option, index) => ({
                  value: String(option.value),
                  label: String(option.text),
                  order: index
                }))}
                selected={(filters.teamAges || []).map(String)}
                onToggle={value => this.onChipFilterToggle('teamAges', value)}
                searchable={ageOptions.length > 12}
                searchPlaceholder="Search ages"
              />
              <PeopleFilterChipSelect
                label="House"
                options={houseOptions.map(option => ({ value: String(option.value), label: String(option.text) }))}
                selected={(filters.houses || []).map(String)}
                onToggle={value => this.onChipFilterToggle('houses', value)}
                collapsedLimit={6}
              />
              <div className="teamsPage__filtersActions">
                <button type="button" className="teamsBtn is-clear" onClick={this.onClearFilterClick}>
                  Clear all
                </button>
                <div>
                  <button type="button" className="teamsBtn" onClick={this.onTableFilterClick}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="teamsBtn teamsBtn--primary is-apply"
                    onClick={this.onApplyFilterClick}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
          {selectedItems.length > 0 && (
            <div className="teamsPage__selection">
              <span>{selectedItems.length} selected</span>
              <div>
                {selectedItems.length === 1 && (
                  <>
                    <button type="button" className="teamsBtn" onClick={this.onEditTeamClick}>
                      Edit
                    </button>
                    <button type="button" className="teamsBtn" onClick={this.onEditTeamMembersClick}>
                      Manage roster
                    </button>
                  </>
                )}
                <button type="button" className="teamsBtn" onClick={this.openReportsModal}>
                  Reports
                </button>
                <button type="button" className="teamsBtn teamsBtn--danger" onClick={this.deleteTeamModalOpen}>
                  Delete teams
                </button>
                <button type="button" className="teamsPage__clear" onClick={this.onUnselectAllClick}>
                  Clear selection
                </button>
              </div>
            </div>
          )}
          <div className="teamsPage__tableScroll">
            <table className="teamsPage__table">
              <thead>
                <tr>
                  <th className="teamsPage__check">
                    <SelectAllCheckbox
                      compact
                      isChecked={items.length > 0 && items.every(team => selectedIds.has(team.id))}
                      totalCount={total}
                      currentPage={currentPage}
                      pageItemsCount={items.length}
                      selectedItems={selectedItems}
                      onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                      onSelectAllClick={this.onSelectAllClick}
                      onUnselectAllClick={this.onUnselectAllClick}
                    />
                  </th>
                  <th>
                    <button onClick={() => this.onTableSortClick('name')}>Team {sortMark('name')}</button>
                  </th>
                  <th>
                    <button onClick={() => this.onTableSortClick('season')}>Season {sortMark('season')}</button>
                  </th>
                  {!hiddenTeamColumns.has('sport') && (
                    <th>
                      <button onClick={() => this.onTableSortClick('sport')}>Sport {sortMark('sport')}</button>
                    </th>
                  )}
                  {!hiddenTeamColumns.has('gender') && <th>Gender</th>}
                  {!hiddenTeamColumns.has('age') && <th>Age</th>}
                  {!hiddenTeamColumns.has('squad') && <th>Squad</th>}
                  {!hiddenTeamColumns.has('house') && <th>House</th>}
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((team, index) => {
                  const isHouseTeam = Boolean((team as any).houseId);
                  const houseName = propz.get(team, ['house', 'name'], 'House team');
                  return (
                    <tr
                      key={team.id}
                      className={`${selectedIds.has(team.id) ? 'is-selected ' : ''}${isHouseTeam ? 'is-house' : ''}`}
                      onClick={() => this.onOpenTeamClick(index)}
                    >
                      <td className="teamsPage__check" onClick={event => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(team.id)}
                          onChange={() => this.onItemClick(index, false)}
                        />
                      </td>
                      <td>
                        <div className="teamsPage__teamCell">
                          <span className={isHouseTeam ? 'is-house' : ''}>{this.renderTeamKitThumbnail(team)}</span>
                          <div>
                            <strong>{team.name}</strong>
                            <small>
                              {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[(team as any).gender] || '—'} ·{' '}
                              {this.getTeamAgesLabel(team)}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="teamsPage__seasonBadge">{team.season || '—'}</span>
                      </td>
                      {!hiddenTeamColumns.has('sport') && (
                        <td>
                          {propz
                            .get(team, ['sports'], [])
                            .map(sport => sport.name)
                            .join(', ') || propz.get(team, ['sport', 'name'], '—')}
                        </td>
                      )}
                      {!hiddenTeamColumns.has('gender') && (
                        <td>{SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[(team as any).gender] || '—'}</td>
                      )}
                      {!hiddenTeamColumns.has('age') && <td>{this.getTeamAgesLabel(team)}</td>}
                      {!hiddenTeamColumns.has('squad') && (
                        <td>
                          <span className="teamsPage__squad">
                            <TeamIcon name="users" /> {(team.players || []).length}
                          </span>
                        </td>
                      )}
                      {!hiddenTeamColumns.has('house') && (
                        <td>
                          {isHouseTeam ? (
                            <span className="teamsPage__houseBadge">
                              <i />
                              {houseName}
                            </span>
                          ) : (
                            <span className="teamsPage__muted">—</span>
                          )}
                        </td>
                      )}
                      <td>
                        <button
                          type="button"
                          className="teamsPage__more"
                          onClick={event => {
                            event.stopPropagation();
                            this.onOpenTeamClick(index);
                          }}
                          aria-label={`Open ${team.name}`}
                        >
                          ›
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumnCount} className="teamsPage__empty">
                      <div>
                        <span>
                          <TeamIcon name="users" size={22} />
                        </span>
                        <strong>{hasActiveFilters ? 'No teams match these filters' : 'No teams yet'}</strong>
                        <p>
                          {hasActiveFilters
                            ? 'Try changing or clearing the active filters.'
                            : 'Create a team to start managing players, kits and fixtures.'}
                        </p>
                        <button
                          type="button"
                          className={`teamsBtn ${hasActiveFilters ? '' : 'teamsBtn--primary'}`}
                          onClick={hasActiveFilters ? this.onClearFilterClick : this.onAddTeamClick}
                        >
                          {hasActiveFilters ? 'Clear filters' : 'Add team'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <nav className="teamsPage__pagination">
              <span>
                Page {currentPage} of {pageCount}
              </span>
              <button disabled={currentPage <= 1} onClick={() => this.setCurrentPageParams(currentPage - 1)}>
                Previous
              </button>
              {pageStart > 1 && (
                <>
                  <button onClick={() => this.setCurrentPageParams(1)}>1</button>
                  {pageStart > 2 && <i>…</i>}
                </>
              )}
              {pages.map(page => (
                <button
                  key={page}
                  className={page === currentPage ? 'is-active' : ''}
                  onClick={() => this.setCurrentPageParams(page)}
                >
                  {page}
                </button>
              ))}
              {pageEnd < pageCount && (
                <>
                  {pageEnd < pageCount - 1 && <i>…</i>}
                  <button onClick={() => this.setCurrentPageParams(pageCount)}>{pageCount}</button>
                </>
              )}
              <button disabled={currentPage >= pageCount} onClick={() => this.setCurrentPageParams(currentPage + 1)}>
                Next
              </button>
            </nav>
          )}
        </section>
      </div>
    );
  }

  renderSendingError(): React.ReactNode {
    const { errorMessage, isError } = this.state;
    if (!isError) {
      return null;
    }

    return (
      <SimpleAlert
        message={errorMessage}
        additionalClassNames={'alert-danger'}
        onCloseAlertClick={this.onCloseErrorClick}
      />
    );
  }

  renderTeamDrawer(): React.ReactNode {
    const { selectedItems, currentTabIndex, isEditTeamPlayers, players } = this.state;
    if (!this.state.isTeamDrawerOpen || selectedItems.length !== 1 || this.state.isAddTeamModalOpen) return null;

    const team = selectedItems[0];
    const houseName = propz.get(team, ['house', 'name']);

    return (
      <>
        <button className="teamsDrawer__scrim" aria-label="Close team details" onClick={this.onUnselectAllClick} />
        <aside className="teamsDrawer" role="dialog" aria-modal="true" aria-label={team.name}>
          <div className="teamsDrawer__head">
            <span className="teamsDrawer__lead">
              <TeamIcon name="whistle" size={22} />
            </span>
            <div>
              <h2>{team.name}</h2>
              <p>
                {(propz.get(team, ['sports'], []) as any[]).map(sport => sport.name).join(', ') ||
                  propz.get(team, ['sport', 'name'], 'Team')}{' '}
                · {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[(team as any).gender] || '—'} · {this.getTeamAgesLabel(team)}
                {team.season ? ` · ${team.season}` : ''}
                {houseName ? ` · ${houseName} house` : ''}
              </p>
            </div>
            <button className="teamsDrawer__close" onClick={this.onUnselectAllClick} aria-label="Close">
              ×
            </button>
          </div>
          <div className="teamsDrawer__tabs">
            {TEAM_TABS.map((tab: any, index) => (
              <button
                key={index}
                className={currentTabIndex === index ? 'is-active' : ''}
                onClick={event => this.onTabClick(event, index)}
              >
                {typeof tab === 'string' ? tab : tab.title || tab.name}
                {index === 1 && <span>{(team.players || []).length}</span>}
              </button>
            ))}
          </div>
          <div className="teamsDrawer__body">{this.getSummaryTabs()}</div>
          <div className="teamsDrawer__foot">
            {currentTabIndex === 1 ? (
              <>
                <button className="teamsBtn" onClick={this.onEditTeamMembersClick}>
                  Manage roster
                </button>
                <button
                  className="teamsBtn teamsBtn--primary"
                  onClick={this.onEditSportAssignmentsClick}
                  disabled={players.length === 0}
                >
                  Edit sport assignments
                </button>
              </>
            ) : (
              <>
                <button className="teamsBtn" onClick={this.onEditTeamMembersClick}>
                  Manage roster
                </button>
                <button className="teamsBtn" onClick={this.onCopyTeamClick}>
                  Copy team
                </button>
                <button className="teamsBtn teamsBtn--primary" onClick={this.onEditTeamClick}>
                  Edit team
                </button>
              </>
            )}
          </div>
        </aside>
      </>
    );
  }

  render() {
    const {
      isLoading,
      isError,
      isAddTeamModalOpen,
      isTeamPerformanceReportModalOpen,
      isTeamFixtureCardsReportModalOpen,
      isReportsModalOpen,
      isDeleteEventModalOpen
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isError ||
      isAddTeamModalOpen ||
      isTeamPerformanceReportModalOpen ||
      isTeamFixtureCardsReportModalOpen ||
      isReportsModalOpen ||
      isDeleteEventModalOpen
        ? 'teamsScreen modal-open'
        : 'teamsScreen';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderTeamForm()}
        {this.renderDeleteTeamConfirmationModal()}
        {this.renderSendingError()}
        {this.renderTeamPerformanceReportFormModal()}
        {this.renderTeamFixtureCardsReportFormModal()}
        {this.renderReportsModal()}
        {this.renderKitModal()}
        <TeamSeasonRolloverModal
          user={this.props.user}
          isOpen={this.state.isRolloverModalOpen}
          currentSeason={getCurrentAcademicYear()}
          initialTeam={this.state.rolloverInitialTeam}
          onClose={() => this.setState({ isRolloverModalOpen: false, rolloverInitialTeam: undefined })}
          onComplete={this.onRolloverComplete}
        />
        {this.renderGrid()}
        {this.renderTeamDrawer()}
      </div>
    );
  }
}
